import {
  useContext,
  createContext,
  FC,
  PropsWithChildren,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { SelectValueType } from 'ui/selects';
import { useExpansionContext } from 'utils/contexts/expansion';
import { CatalogQueryDetails, CatalogData } from '../types/catalogQuery';
import { isDefined } from 'utils/tsHelpers';

export type Props<T extends SelectValueType | CatalogData> = {
  onChange: (val: T) => void;
};

export type CatalogQueryContext = {
  catalogQueryDetails?: CatalogQueryDetails;
  onOpen: (data: CatalogQueryDetails) => void;
  onClose: (data: Partial<CatalogQueryDetails>) => void;
  resetCatalogQuery: () => void;
  isReadOnlyOnOpen: boolean;
};

export const defaultState = {
  onClose: () => null,
  onOpen: () => null,
  resetCatalogQuery: () => null,
  isReadOnlyOnOpen: false,
};

export const CatalogQueryContext = createContext<CatalogQueryContext>(defaultState);

export const useCatalogQueryContext = () => useContext(CatalogQueryContext);

export const CatalogQueryContextProvider: FC<PropsWithChildren<Props<CatalogData>>> = ({
  children,
  onChange,
}) => {
  const [catalogQueryDetails, setCatalogQueryDetails] = useState<CatalogQueryDetails>();
  const { expand, collapse } = useExpansionContext();

  const onOpen = useCallback((conditionData: CatalogQueryDetails) => {
    setCatalogQueryDetails(conditionData);
    expand();
  }, []);

  const onClose = useCallback(({ catalogField, catalogQuery }: Partial<CatalogQueryDetails>) => {
    setCatalogQueryDetails((prev) => ({ ...prev, catalogField, catalogQuery }));
    if (catalogQuery) onChange({ ...catalogQueryDetails, catalogField, catalogQuery });
    collapse();
  }, []);

  const resetCatalogQuery = useCallback(() => {
    setCatalogQueryDetails((prev) => ({ ...prev, catalogQuery: undefined }));
  }, []);

  const isReadOnlyOnOpen = useMemo(
    () => isDefined(catalogQueryDetails?.catalogQuery),
    [catalogQueryDetails],
  );

  const value = useMemo(
    () => ({
      catalogQueryDetails,
      resetCatalogQuery,
      onClose,
      onOpen,
      isReadOnlyOnOpen,
    }),
    [catalogQueryDetails, resetCatalogQuery, onClose, onOpen, isReadOnlyOnOpen],
  );

  return <CatalogQueryContext.Provider value={value}>{children}</CatalogQueryContext.Provider>;
};
